import React, { useState } from 'react';
import ILink from '../../types/ILink';
import { isNonEmptyArray } from '../../lib/typeGuards';
import { PageInteractions, PageInteractionsProps } from './partials/PageInteractions';
import { isDeletedUser, UserProfile } from '../../types/api/UserProfile';
import { ConfidentialLevel } from '../../types/api/ConfidentialLevel';
import { DefaultView, Views } from '../../types/BlockWithViews';
import { useSonTranslation } from '../../translation/useSonTranslation';
import {
  PersonProfileImageSmall,
  PersonProfileImageSmallInfoCard,
} from '../../components/molecules/PersonProfileImageSmall';
import { PageMetaInfoOverviewTerms } from './partials/PageMetaInfoOverviewTerms';
import { Icons, SVG } from '../../components/atoms/SVG';
import { ConfidentialLevelDefinition } from './partials/ConfidentialLevelDefinition';
import { BADGE_STATUS, StatusBadge } from '../../_lib/components/Badge';
import { asFetchableBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { MetaInfoPage } from '../../_lib/components/MetaInfo';
import { EditorialResponsibleProps } from '../../_lib/components/MetaInfo/MetaInfoPage';
import bem from '../../lib/bemUtils/bem';

function hasKeywords(keywords?: ILink[]) {
  return keywords && isNonEmptyArray(keywords);
}

export type PageMetaInfoDefaultViewProps = {
  pageInteractions?: PageInteractionsProps;
  author?: UserProfile;
  editDate?: string;
  editedBy?: ILink;
  createdBy?: ILink;
  createDate?: string;
  contentDate?: string;
  publishedBy?: ILink;
  publicationDate?: string;
  validUntil?: string;
  validityExpired?: boolean;
  confidentialLevel?: ConfidentialLevel;
  archived?: boolean;
  archiveDate?: string;
  changeComment?: string;
  keywords?: ILink[];
  editorialResponsible?: EditorialResponsibleProps;
};

export type PageMetaInfoProps = Views<DefaultView<PageMetaInfoDefaultViewProps | null>>;

function PageMetaInfo({ views }: PageMetaInfoProps) {
  const { t } = useSonTranslation('MetaInfo');
  const [isOpen, setIsOpen] = useState(false);
  const embedded = views?.default?.embedded;

  if (!embedded) {
    return null;
  }
  const {
    author,
    contentDate,
    archiveDate,
    archived,
    confidentialLevel,
    publishedBy,
    publicationDate,
    editDate,
    editedBy,
    createDate,
    createdBy,
    keywords,
    validUntil,
    validityExpired,
    changeComment,
    pageInteractions,
    editorialResponsible,
  } = embedded;
  const flyoutId = 'flyout-pagemetainfo';

  const togglePageMetaInfo = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const isExpandable = !!(
    changeComment ||
    publicationDate ||
    editDate ||
    editedBy ||
    createDate ||
    createdBy ||
    hasKeywords(keywords) ||
    validUntil ||
    validityExpired ||
    editorialResponsible
  );

  /**
   *  When an author is supplied, the whole container needs different padding
   *  :has is not available in old browsers like Firefox ESR 115
   */
  const containerClass = () => {
    if (author) {
      return 'page-meta-info__header-container page-meta-info__header-container--with-image';
    }
    return 'page-meta-info__header-container';
  };

  return (
    <section className="page-meta-info" aria-labelledby="page-meta-info-headline">
      <h2 className="invisible" id="page-meta-info-headline">{t('info')}</h2>
      <div className="page-meta-info__block">

        <div className={containerClass()}>
          <div className="page-meta-info__author">
            {author && (
              <div className="page-meta-info__imagecontainer">
                {isDeletedUser(author)
                  ? (
                      <PersonProfileImageSmall
                        profileImageUrl={author.profileImageUrl}
                        profileName={author.profileName}
                      />
                    )
                  : (
                      <PersonProfileImageSmallInfoCard
                        profileImageUrl={author.profileImageUrl}
                        profileName={author.profileName}
                        personInfoCardUrl={author.personInfoCardUrl}
                      />
                    )}
              </div>
            )}
            <PageMetaInfoOverviewTerms contentDate={contentDate} author={author} />
          </div>
          {isExpandable && (
            <button
              onClick={togglePageMetaInfo}
              aria-controls={flyoutId}
              className="page-meta-info__toggle font-regular-15-19"
              aria-expanded={isOpen}
              type="button"
            >
              <span className="page-meta-info__toggle-text">{t('pageInformation')}</span>
              <span className="page-meta-info__toggle-icon-container">
                <SVG svgSpriteId={Icons.ARROW_DOWN} svgClass="page-meta-info__toggle-icon" />
              </span>
            </button>
          )}
          {confidentialLevel && <ConfidentialLevelDefinition confidentialLevel={confidentialLevel} />}
          {archived && <StatusBadge status={BADGE_STATUS.ARCHIVED} archiveDate={archiveDate} />}
        </div>
        <div
          id={flyoutId}
          className={bem('page-meta-info__flyout', author || editorialResponsible ? 'author' : null)}
          aria-hidden={!isOpen}
        >
          <div className="page-meta-info__flyout-content">
            <MetaInfoPage
              changeComment={changeComment}
              publishedBy={publishedBy}
              publicationDate={publicationDate}
              editDate={editDate}
              editedBy={editedBy}
              createDate={createDate}
              createdBy={createdBy}
              keywords={keywords}
              validUntil={validUntil}
              validityExpired={validityExpired}
              confidentialLevel={confidentialLevel}
              editorialResponsible={editorialResponsible}
            />
          </div>
        </div>
      </div>
      {pageInteractions &&
        <div className="page-meta-info__page-interactions">
          <PageInteractions {...pageInteractions} />
        </div>}
    </section>
  );
}

export const PageMetaInfoBlock = asFetchableBlock(PageMetaInfo, BlockType.pagemetainfo, BlockType.pagemetainfo);
